import { Add, CalendarToday, Cancel, CheckCircle, Close, Info, Save, School } from '@mui/icons-material';
import
    {
        Alert,
        Avatar,
        Box,
        Button,
        ButtonGroup,
        Card,
        CardContent,
        CardHeader,
        Chip,
        CircularProgress,
        Collapse,
        Divider,
        FormControl,
        FormHelperText,
        IconButton,
        InputLabel,
        List,
        ListItem,
        ListItemText,
        MenuItem,
        Paper,
        Popover,
        Select,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        TextField,
        Typography,
        useMediaQuery,
        useTheme,
    } from '@mui/material';
import { alpha } from '@mui/material/styles';
import
    {
        setEmployeeDescription,
        setEmployeeNames,
        setEmployeeRoles,
        setEmployeeSkills,
    } from '@store/reducer/employeeReducer';
import { setFirstExperience } from '@store/reducer/employeeSettingsReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// COMMENTED OUT FOR MOCK:
// import createAndMapSkill from '@src/APIs/graphQl/Skill/createAndMapSkill';
// import changeMapRole from '@src/APIs/graphQl/Role/changeMapRole';
// import cofigureWantedCompetence from '@src/APIs/graphQl/Employee/WantedCompetences/cofigureWantedCompetence';
// import updateMyGenderAndName from '@src/APIs/graphQl/Employee/updateMyGenderAndName';
// import updateMyDescription from '@src/APIs/graphQl/Employee/updateMyDescription';

import FuturisticDatePicker from '../../../_UnderConstruction/FuturisticDatePicker/FuturisticDatePicker';
import SpaceGradientButton from '../components/SpaceGradientButton';
import { mockCertificates, mockSkills } from '../data/mockAnalysisData';
import { useOnboardingStrings } from '../data/OnboardingLangStrings';
import { getSkillLevelInfo, getTextColor, SKILL_LEVELS } from '../data/skillLevels';
import type {
    CertificateItem,
    CVAnalysisResultsStepProps,
    OnboardingData,
    PersonalInfo,
    RoleItem,
    SkillItem,
} from '../types/OnboardingTypes';

import MobileCVAnalysisResultsStep from './MobileCVDialog';

const CVAnalysisResultsStep: React.FC<CVAnalysisResultsStepProps & { data?: OnboardingData }> = ({
    onNext,
    onBack,
    setAgentMessage,
    currentSubStep,
    data,
    onDataUpdate,
}) =>
{
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const strings = useOnboardingStrings('de');
    const dispatch = useDispatch();
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);

    const currentSubsection = currentSubStep;
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const allCompetences = useSelector((state: AppState) => state.company.allCompetences);
    const allRoles = useSelector((state: AppState) => state.company.allRoles);
    const employee = useSelector((state: AppState) => state.employee);

    const [expandedSections, setExpandedSections] = useState({
        skills: false,
        certificates: false,
        roles: false,
        superPowers: false,
    });
    const [loading, setLoading] = useState({
        skills: false,
        certificates: false,
        roles: false,
        superPowers: false,
    });
    const [errors, setErrors] = useState({
        skills: '',
        certificates: '',
        roles: '',
        superPowers: '',
    });
    const [inlineForm, setInlineForm] = useState({
        skill: { name: '', level: 1 as const },
        certificate: '',
        role: { name: '', description: '' },
    });
    const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
        firstName: employee?.firstName || '',
        lastName: employee?.lastName || '',
        shortDescription: employee?.description || '',
    });

    const [superPowers, setSuperPowers] = useState<SkillItem[]>([]);

    const [roles, setRoles] = useState<RoleItem[]>([]);

    const [skills, setSkills] = useState<SkillItem[]>([]);

    const [certificates, setCertificates] = useState<CertificateItem[]>([]);

    const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () =>
    {
        setInfoAnchorEl(null);
    };

    const infoOpen = Boolean(infoAnchorEl);

    const handleStatusChange = (
        type: 'skills' | 'certificates' | 'roles' | 'superPowers',
        id: number,
        newStatus: 'pending' | 'accepted' | 'rejected',
    ) =>
    {
        if (type === 'skills')
        {
            const updatedItems = skills.map((item) => (item.id === id ? { ...item, status: newStatus } : item));
            setSkills(updatedItems);
        } else if (type === 'superPowers')
        {
            const updatedItems = superPowers.map((item) => (item.id === id ? { ...item, status: newStatus } : item));
            setSuperPowers(updatedItems);
        } else if (type === 'certificates')
        {
            const updatedItems = certificates.map((item) =>
            {
                if (item.id === id)
                {
                    const updatedItem = { ...item, status: newStatus };
                    if (newStatus === 'accepted' && !updatedItem.startDate)
                    {
                        updatedItem.startDate = new Date();
                    }
                    return updatedItem;
                }
                return item;
            });
            setCertificates(updatedItems);
        } else if (type === 'roles')
        {
            const updatedItems = roles.map((item) => (item.id === id ? { ...item, status: newStatus } : item));
            setRoles(updatedItems);
        }
    };

    const handleCertificateDateChange = (certId: number, field: 'startDate' | 'endDate', date: Date | undefined) =>
    {
        const updatedCertificates = certificates.map((cert) =>
            cert.id === certId ? { ...cert, [field]: date } : cert,
        );
        setCertificates(updatedCertificates);

        if (onDataUpdate)
        {
            onDataUpdate({
                personalInfo,
                skills,
                certificates: updatedCertificates,
                roles,
            });
        }
    };

    const handleSkillLevelChange = (skillId: number, newLevel: 1 | 2 | 3 | 4) =>
    {
        const updatedSkills = skills.map((skill) => (skill.id === skillId ? { ...skill, level: newLevel } : skill));
        setSkills(updatedSkills);

        // Update parent data
        if (onDataUpdate)
        {
            onDataUpdate({
                personalInfo,
                skills: updatedSkills,
                certificates,
                roles,
            });
        }
    };

    const groupedSkills = skills.reduce(
        (acc, skill) =>
        {
            if (!acc[skill.category])
            {
                acc[skill.category] = [];
            }
            acc[skill.category].push(skill);
            return acc;
        },
        {} as Record<string, SkillItem[]>,
    );

    // Subsection messages for KI assistant
    const subsectionMessages = [
        strings.personalInfoMessage,
        strings.skillsMessage,
        strings.superPowersMessage,
        strings.certificatesMessage,
        strings.rolesMessage,
    ];

    // Initialize data from props only once
    useEffect(() =>
    {
        if (data?.analysisResults && skills.length === 0)
        {
            setSkills(data.analysisResults.skills || []);
            setCertificates(data.analysisResults.certificates || []);
            setRoles(data.analysisResults.roles || []);
            // Initialize SuperPowers with top 3 skills
            const topSkills = (data.analysisResults.skills || [])
                .filter((skill) => skill.level >= 3)
                .sort((a, b) => b.level - a.level)
                .slice(0, 3);
            setSuperPowers(topSkills);
        } else if (skills.length === 0)
        {
            // Use mock data from separate file
            setSkills(mockSkills);
            setCertificates(mockCertificates);
        }
        if (data?.personalInfo?.shortDescription && !personalInfo.shortDescription)
        {
            setPersonalInfo((prev) => ({
                ...prev,
                shortDescription: data.personalInfo.shortDescription,
            }));
        }
    }, []);

    // Update parent data whenever local state changes
    useEffect(() =>
    {
        const timeoutId = setTimeout(() =>
        {
            if (onDataUpdate)
            {
                onDataUpdate({
                    personalInfo,
                    skills,
                    certificates,
                    roles,
                    superPowers,
                });
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [personalInfo, skills, certificates, roles, superPowers]);

    useEffect(() =>
    {
        setAgentMessage(subsectionMessages[currentSubsection]);
    }, [currentSubsection, setAgentMessage]);

    // Use mobile version for mobile devices
    if (isMobile)
    {
        return (
            <MobileCVAnalysisResultsStep
                onNext={onNext}
                onBack={onBack}
                setAgentMessage={setAgentMessage}
                currentSubStep={currentSubStep}
                onDataUpdate={onDataUpdate}
            />
        );
    }

    // Add handlers for manual additions
    const handleAddSkill = async () =>
    {
        const skillData = inlineForm.skill;
        if (!skillData.name.trim())
        {
            setErrors((prev) => ({ ...prev, skills: 'Bitte wählen Sie eine Fähigkeit aus' }));
            return;
        }
        setLoading((prev) => ({ ...prev, skills: true }));

        try
        {
            // MOCK: Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 500));
            const newSkill: SkillItem = {
                id: Date.now(), // Mock ID
                name: skillData.name,
                category: 'Hard Skills',
                level: skillData.level,
                status: 'accepted',
                type: 'skill',
                source: 'user',
            };
            setSkills((prev) => [...prev, newSkill]);
            setInlineForm((prev) => ({ ...prev, skill: { name: '', level: 1 } }));
            setExpandedSections((prev) => ({ ...prev, skills: false }));
        } catch (error)
        {
            setErrors((prev) => ({ ...prev, skills: 'Fehler beim Hinzufügen der Fähigkeit' }));
        }
        setLoading((prev) => ({ ...prev, skills: false }));
    };

    const toggleExpansion = (section: keyof typeof expandedSections) =>
    {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
        setErrors((prev) => ({ ...prev, [section]: '' }));
    };

    // Add handlers for manual additions

    // STEP 1: PERSONAL INFORMATION
    const renderPersonalInfoSection = () => (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: 600,
                            }}
                        >
                            1
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 400 }}>
                            {strings.personalInfo}
                        </Typography>
                    </Box>
                }
                sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2,
                }}
            />
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label={strings.firstName}
                            value={personalInfo.firstName}
                            onChange={(e) =>
                            {
                                const updatedInfo = { ...personalInfo, firstName: e.target.value };
                                setPersonalInfo(updatedInfo);
                                if (onDataUpdate)
                                {
                                    onDataUpdate({ personalInfo: updatedInfo, skills, certificates, roles });
                                }
                            }}
                            variant="outlined"
                            required
                            aria-label={strings.firstName}
                        />
                        <TextField
                            fullWidth
                            label={strings.lastName}
                            value={personalInfo.lastName}
                            onChange={(e) =>
                            {
                                const updatedInfo = { ...personalInfo, lastName: e.target.value };
                                setPersonalInfo(updatedInfo);
                                if (onDataUpdate)
                                {
                                    onDataUpdate({ personalInfo: updatedInfo, skills, certificates, roles });
                                }
                            }}
                            variant="outlined"
                            required
                            aria-label={strings.lastName}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Firmen E-Mail"
                            value={personalInfo.email || 'max.mustermann@firma.de'}
                            variant="outlined"
                            type="email"
                            disabled
                            aria-label="Firmen E-Mail"
                        />
                        <TextField
                            fullWidth
                            label="Telefonnummer"
                            value={personalInfo.phone || '+49 123 456789'}
                            onChange={(e) =>
                            {
                                const updatedInfo = { ...personalInfo, phone: e.target.value };
                                setPersonalInfo(updatedInfo);
                                if (onDataUpdate)
                                {
                                    onDataUpdate({ personalInfo: updatedInfo, skills, certificates, roles });
                                }
                            }}
                            variant="outlined"
                            type="tel"
                            aria-label="Telefonnummer"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Geschlecht</InputLabel>
                            <Select
                                value={personalInfo.gender || ''}
                                label="Geschlecht"
                                onChange={(e) =>
                                {
                                    const updatedInfo = {
                                        ...personalInfo,
                                        gender: e.target.value as 'male' | 'female' | 'other' | 'prefer-not-to-say',
                                    };
                                    setPersonalInfo(updatedInfo);
                                    if (onDataUpdate)
                                    {
                                        onDataUpdate({ personalInfo: updatedInfo, skills, certificates, roles });
                                    }
                                }}
                                aria-label="Geschlecht"
                            >
                                <MenuItem value="male">Männlich</MenuItem>
                                <MenuItem value="female">Weiblich</MenuItem>
                                <MenuItem value="other">Divers</MenuItem>
                                <MenuItem value="prefer-not-to-say">Keine Angabe</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField
                        fullWidth
                        label={strings.shortDescription}
                        value={personalInfo.shortDescription}
                        onChange={(e) =>
                        {
                            const updatedInfo = { ...personalInfo, shortDescription: e.target.value };
                            setPersonalInfo(updatedInfo);
                            if (onDataUpdate)
                            {
                                onDataUpdate({ personalInfo: updatedInfo, skills, certificates, roles });
                            }
                        }}
                        multiline
                        rows={3}
                        helperText={strings.shortDescriptionPlaceholder}
                    />
                </Box>
            </CardContent>
        </Card>
    );

    // STEP 2: SKILLS
    const renderSkillsSection = () => (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: 600,
                            }}
                        >
                            2
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 400 }}>
                            {strings.suggestedSkills}
                        </Typography>
                        <IconButton size="small" sx={{ ml: 1 }} onClick={handleInfoClick}>
                            <Info fontSize="small" />
                        </IconButton>
                        <Popover
                            open={infoOpen}
                            anchorEl={infoAnchorEl}
                            onClose={handleInfoClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <Box sx={{ p: 2, maxWidth: 300 }}>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Du kannst Fähigkeiten zuweisen und eine Selbsteinschätzung durchführen, die zu einem
                                    Level von Junior bis Expert führt.
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                                    >
                                        Skill-Level Bewertung:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9 }}>
                                        {SKILL_LEVELS.map((level) => (
                                            <Box
                                                key={level.level}
                                                sx={{
                                                    flex: '1 1 45%',
                                                    p: 1,
                                                    borderRadius: 0.5,
                                                    backgroundColor: level.color,
                                                    color: getTextColor(level.level),
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontSize: '0.8rem', fontWeight: 400 }}
                                                >
                                                    {level.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Popover>
                    </Box>
                }
                sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2,
                }}
            />
            <CardContent>
                <Alert
                    severity="info"
                    icon={<Info sx={{ color: theme.palette.info.main }} />}
                    sx={{
                        mb: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        border: `1px solid ${theme.palette.info.main}`,
                        '& .MuiAlert-message': {
                            color: theme.palette.info.main,
                        },
                    }}
                >
                    <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                        Wähle mindestens eine Fähigkeit aus und bewerte dein Level. Du kannst weitere Fähigkeiten
                        hinzufügen.
                    </Typography>
                </Alert>
                {Object.entries(groupedSkills)
                    .sort(([categoryA], [categoryB]) =>
                    {
                        const order = ['Soft Skills', 'Hard Skills', 'Industry Skills'];
                        const indexA = order.indexOf(categoryA);
                        const indexB = order.indexOf(categoryB);
                        if (indexA === -1 && indexB === -1) return categoryA.localeCompare(categoryB);
                        if (indexA === -1) return 1;
                        if (indexB === -1) return -1;
                        return indexA - indexB;
                    })
                    .map(([category, categorySkills]) => (
                        <Box key={category} sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    mb: 1.5,
                                    fontSize: '1.1rem',
                                }}
                            >
                                {category}
                            </Typography>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Skill
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Level
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Anpassen
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categorySkills.map((skill) =>
                                        {
                                            const levelInfo = getSkillLevelInfo(skill.level);
                                            return (
                                                <TableRow
                                                    key={skill.id}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{skill.name}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={levelInfo.name}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: levelInfo.color,
                                                                color: getTextColor(skill.level),
                                                                fontWeight: 400,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <ButtonGroup size="small" variant="outlined">
                                                            {SKILL_LEVELS.map((level) => (
                                                                <Button
                                                                    key={level.level}
                                                                    onClick={() =>
                                                                        handleSkillLevelChange(
                                                                            skill.id,
                                                                            level.level as 1 | 2 | 3 | 4,
                                                                        )
                                                                    }
                                                                    variant={
                                                                        skill.level === level.level
                                                                            ? 'contained'
                                                                            : 'outlined'
                                                                    }
                                                                    sx={{
                                                                        minWidth: 32,
                                                                        px: 1,
                                                                        backgroundColor:
                                                                            skill.level === level.level
                                                                                ? level.color
                                                                                : undefined,
                                                                        color:
                                                                            skill.level === level.level
                                                                                ? getTextColor(level.level)
                                                                                : 'text.primary',
                                                                        '&:hover': {
                                                                            backgroundColor: level.color,
                                                                            color: getTextColor(level.level),
                                                                        },
                                                                    }}
                                                                >
                                                                    {level.level}
                                                                </Button>
                                                            ))}
                                                        </ButtonGroup>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {skill.status === 'pending' && (
                                                                <>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            color: '#0BB140',
                                                                            borderColor: '#0BB140',
                                                                            '&:hover': {
                                                                                backgroundColor: '#0BB140',
                                                                                color: 'white',
                                                                            },
                                                                        }}
                                                                        onClick={() =>
                                                                            handleStatusChange(
                                                                                'skills',
                                                                                skill.id,
                                                                                'accepted',
                                                                            )
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            color: theme.palette.primary.main,
                                                                            borderColor: theme.palette.primary.main,
                                                                            '&:hover': {
                                                                                backgroundColor:
                                                                                    theme.palette.primary.main,
                                                                                color: 'white',
                                                                            },
                                                                        }}
                                                                        onClick={() =>
                                                                            handleStatusChange(
                                                                                'skills',
                                                                                skill.id,
                                                                                'rejected',
                                                                            )
                                                                        }
                                                                    >
                                                                        ✗
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {skill.status === 'accepted' && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    className="status-chip-accepted"
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleStatusChange(
                                                                            'skills',
                                                                            skill.id,
                                                                            'pending',
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                            {skill.status === 'rejected' && (
                                                                <Chip
                                                                    icon={<Cancel />}
                                                                    label="✗"
                                                                    className="status-chip-rejected"
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleStatusChange(
                                                                            'skills',
                                                                            skill.id,
                                                                            'pending',
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))}

                {/* Inline skill addition */}
                <Collapse in={expandedSections.skills}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Neue Fähigkeit hinzufügen
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small" error={!!errors.skills}>
                                <InputLabel>Fähigkeit auswählen</InputLabel>
                                <Select
                                    value={inlineForm.skill.name}
                                    label="Fähigkeit auswählen"
                                    onChange={(e) =>
                                    {
                                        setInlineForm((prev) => ({
                                            ...prev,
                                            skill: { ...prev.skill, name: e.target.value },
                                        }));
                                        setErrors((prev) => ({ ...prev, skills: '' }));
                                    }}
                                >
                                    {allSkills.map((skill) => (
                                        <MenuItem key={skill.id} value={skill.title}>
                                            {skill.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.skills && <FormHelperText>{errors.skills}</FormHelperText>}
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <SpaceGradientButton
                                    size="small"
                                    onClick={handleAddSkill}
                                    disabled={!inlineForm.skill.name || loading.skills}
                                    startIcon={loading.skills ? <CircularProgress size={16} /> : null}
                                >
                                    {loading.skills ? 'Hinzufügen...' : 'Hinzufügen'}
                                </SpaceGradientButton>
                                <Button variant="outlined" size="small" onClick={() => toggleExpansion('skills')}>
                                    Abbrechen
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Collapse>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={expandedSections.skills ? <Close /> : <Add />}
                        onClick={() => toggleExpansion('skills')}
                        sx={{
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                            },
                        }}
                    >
                        {expandedSections.skills ? 'Schließen' : 'Hinzufügen'}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    // STEP 3: SUPERPOWERS
    const renderSuperPowersSection = () =>
    {
        const availableSkills = skills.filter((skill) => !superPowers.some((sp) => sp.id === skill.id));

        const handleSuperPowerToggle = (skill: SkillItem) =>
        {
            if (superPowers.some((sp) => sp.id === skill.id))
            {
                setSuperPowers((prev) => prev.filter((sp) => sp.id !== skill.id));
            } else if (superPowers.length < 3)
            {
                setSuperPowers((prev) => [...prev, { ...skill, status: 'accepted' }]);
            }
        };

        const handleAddSuperPower = async () =>
        {
            const skillData = inlineForm.skill;
            if (!skillData.name.trim())
            {
                setErrors((prev) => ({ ...prev, superPowers: 'Bitte wählen Sie eine Fähigkeit aus' }));
                return;
            }

            const selectedSkill = skills.find((s) => s.name === skillData.name);
            if (selectedSkill && superPowers.length < 3)
            {
                setSuperPowers((prev) => [...prev, { ...selectedSkill, status: 'accepted' }]);
                setInlineForm((prev) => ({ ...prev, skill: { name: '', level: 1 } }));
                setExpandedSections((prev) => ({ ...prev, superPowers: false }));
            }
        };

        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                3
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                Ihre SuperPowers
                            </Typography>
                        </Box>
                    }
                    sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 2,
                    }}
                />
                <CardContent>
                    <Alert
                        severity="info"
                        icon={<Info sx={{ color: theme.palette.info.main }} />}
                        sx={{
                            mb: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${theme.palette.info.main}`,
                            '& .MuiAlert-message': {
                                color: theme.palette.info.main,
                            },
                        }}
                    >
                        <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                            Wählen Sie bis zu 3 Ihrer stärksten Fähigkeiten als SuperPowers aus.
                        </Typography>
                    </Alert>

                    {/* Selected SuperPowers as List */}
                    <List sx={{ p: 0 }}>
                        {superPowers.map((superPower) =>
                        {
                            const levelInfo = getSkillLevelInfo(superPower.level);
                            return (
                                <ListItem key={superPower.id} sx={{ px: 0, py: 1 }}>
                                    <ListItemText
                                        primary={superPower.name}
                                        secondary={`${levelInfo.name} - ${superPower.category}`}
                                    />
                                    <Button
                                        size="small"
                                        onClick={() => handleSuperPowerToggle(superPower)}
                                        sx={{ minWidth: 'auto', px: 1 }}
                                    >
                                        <Close sx={{ fontSize: 16 }} />
                                    </Button>
                                </ListItem>
                            );
                        })}
                    </List>

                    {superPowers.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Keine SuperPowers ausgewählt
                        </Typography>
                    )}

                    {/* Inline SuperPower addition */}
                    <Collapse in={expandedSections.superPowers}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                SuperPower hinzufügen
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormControl fullWidth size="small" error={!!errors.superPowers}>
                                    <InputLabel>Fähigkeit auswählen</InputLabel>
                                    <Select
                                        value={inlineForm.skill.name}
                                        label="Fähigkeit auswählen"
                                        onChange={(e) =>
                                        {
                                            setInlineForm((prev) => ({
                                                ...prev,
                                                skill: { ...prev.skill, name: e.target.value },
                                            }));
                                            setErrors((prev) => ({ ...prev, superPowers: '' }));
                                        }}
                                    >
                                        {availableSkills.map((skill) => (
                                            <MenuItem key={skill.id} value={skill.name}>
                                                {skill.name} ({getSkillLevelInfo(skill.level).name})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.superPowers && <FormHelperText>{errors.superPowers}</FormHelperText>}
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <SpaceGradientButton
                                        size="small"
                                        onClick={handleAddSuperPower}
                                        disabled={
                                            !inlineForm.skill.name || loading.superPowers || superPowers.length >= 3
                                        }
                                        startIcon={loading.superPowers ? <CircularProgress size={16} /> : null}
                                    >
                                        {loading.superPowers ? 'Hinzufügen...' : 'Hinzufügen'}
                                    </SpaceGradientButton>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => toggleExpansion('superPowers')}
                                    >
                                        Abbrechen
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>

                    {/* Add button */}
                    {superPowers.length < 3 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={expandedSections.superPowers ? <Close /> : <Add />}
                                onClick={() => toggleExpansion('superPowers')}
                                sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                    },
                                }}
                            >
                                {expandedSections.superPowers ? 'Schließen' : 'Hinzufügen'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    // STEP 4: CERTIFICATES
    const renderCertificatesSection = () => (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: 600,
                            }}
                        >
                            4
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 400 }}>
                            {strings.suggestedCertificates}
                        </Typography>
                    </Box>
                }
                sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2,
                }}
            />
            <CardContent>
                <Alert
                    severity="info"
                    icon={<Info sx={{ color: theme.palette.info.main }} />}
                    sx={{
                        mb: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        border: `1px solid ${theme.palette.info.main}`,
                        '& .MuiAlert-message': {
                            color: theme.palette.info.main,
                        },
                    }}
                >
                    <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                        Bestätigen Sie Ihre Zertifikate und geben Sie Start- und Ablaufdatum an.
                    </Typography>
                </Alert>
                <List sx={{ p: 0 }}>
                    {certificates.map((item, index) =>
                    {
                        const getCertificateLogo = (cert: any) =>
                        {
                            if (cert.customLogo) return cert.customLogo;
                            const name = cert.name.toLowerCase();
                            if (name.includes('aws')) return '/certificates/aws-logo.png';
                            if (name.includes('react')) return '/certificates/react-logo.svg';
                            if (name.includes('scrum') || name.includes('agile')) return '/certificates/scrum-logo.png';
                            // Fallback to AWS logo as default placeholder
                            return '/certificates/aws-logo.png';
                        };

                        return (
                            <React.Fragment key={item.id}>
                                <ListItem
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        bgcolor:
                                            index % 2 === 0 ? 'transparent' : alpha(theme.palette.primary.main, 0.02),
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                    }}
                                >
                                    {/* Certificate Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            mb: item.status === 'accepted' ? 1 : 0,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, flex: 1 }}>
                                            <Avatar
                                                src={getCertificateLogo(item)}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                }}
                                            >
                                                <School sx={{ fontSize: 14 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">{item.name}</Typography>
                                                {item.datesConfirmed && item.endDate && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', fontStyle: 'italic' }}
                                                    >
                                                        Ablauf: {item.endDate.toLocaleDateString('de-DE')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            {item.status === 'pending' && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            color: '#0BB140',
                                                            borderColor: '#0BB140',
                                                            '&:hover': {
                                                                backgroundColor: '#0BB140',
                                                                color: 'white',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            handleStatusChange('certificates', item.id, 'accepted')
                                                        }
                                                    >
                                                        Annehmen
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            borderColor: theme.palette.primary.main,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.primary.main,
                                                                color: 'white',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            handleStatusChange('certificates', item.id, 'rejected')
                                                        }
                                                    >
                                                        Ablehnen
                                                    </Button>
                                                </>
                                            )}
                                            {item.status === 'accepted' && (
                                                <Chip
                                                    icon={<CheckCircle />}
                                                    label="Angenommen"
                                                    className="status-chip-accepted"
                                                    size="small"
                                                    onClick={() =>
                                                        handleStatusChange('certificates', item.id, 'pending')
                                                    }
                                                />
                                            )}
                                            {item.status === 'rejected' && (
                                                <Chip
                                                    icon={<Cancel />}
                                                    label="Abgelehnt"
                                                    className="status-chip-rejected"
                                                    size="small"
                                                    onClick={() =>
                                                        handleStatusChange('certificates', item.id, 'pending')
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Certificate Dates - Only show when accepted and not confirmed */}
                                    {item.status === 'accepted' && !item.datesConfirmed && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: alpha('#0BB140', 0.05),
                                                border: `1px solid ${alpha('#0BB140', 0.2)}`,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <CalendarToday sx={{ fontSize: 16, color: '#0BB140' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#0BB140' }}>
                                                    Zertifikatsdaten
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                                                    >
                                                        Startdatum *
                                                    </Typography>
                                                    <FuturisticDatePicker
                                                        value={item.startDate}
                                                        onChange={(date) =>
                                                            handleCertificateDateChange(item.id, 'startDate', date)
                                                        }
                                                        placeholder="Startdatum auswählen"
                                                        size="small"
                                                    />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                                                    >
                                                        Ablaufdatum
                                                    </Typography>
                                                    <FuturisticDatePicker
                                                        value={item.endDate}
                                                        onChange={(date) =>
                                                            handleCertificateDateChange(item.id, 'endDate', date)
                                                        }
                                                        placeholder="Ablaufdatum auswählen"
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mt: 1.5,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontStyle: 'italic' }}
                                                >
                                                    Geben Sie das Datum an, wann Sie das Zertifikat erhalten haben
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<Save />}
                                                    onClick={() =>
                                                    {
                                                        // Mark dates as confirmed
                                                        const updatedCertificates = certificates.map((cert) =>
                                                            cert.id === item.id
                                                                ? { ...cert, datesConfirmed: true }
                                                                : cert,
                                                        );
                                                        setCertificates(updatedCertificates);

                                                        // Trigger data update to confirm the dates
                                                        if (onDataUpdate)
                                                        {
                                                            onDataUpdate({
                                                                personalInfo,
                                                                skills,
                                                                certificates: updatedCertificates,
                                                                roles,
                                                            });
                                                        }
                                                    }}
                                                    sx={{
                                                        background: 'linear-gradient(45deg, #0BB140 30%, #0a9e3a 90%)',
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        boxShadow: `0 2px 8px ${alpha('#0BB140', 0.3)}`,
                                                        '&:hover': {
                                                            background:
                                                                'linear-gradient(45deg, #0a9e3a 30%, #098c33 90%)',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px ${alpha('#0BB140', 0.4)}`,
                                                        },
                                                    }}
                                                >
                                                    Bestätigen
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </ListItem>
                                {index < certificates.length - 1 && <Divider sx={{ mx: 2 }} />}
                            </React.Fragment>
                        );
                    })}
                </List>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 2,
                        fontStyle: 'italic',
                        opacity: 0.8,
                    }}
                >
                    Alle Daten können später noch im Profil geändert werden.
                </Typography>
            </CardContent>
        </Card>
    );

    // STEP 5: ROLES
    const renderRolesSection = () => (
        <Card sx={{ mb: 3 }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: '1rem',
                                fontWeight: 600,
                            }}
                        >
                            5
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 400 }}>
                            {strings.suggestedRoles}
                        </Typography>
                    </Box>
                }
                sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2,
                }}
            />
            <CardContent>
                <Alert
                    severity="info"
                    icon={<Info sx={{ color: theme.palette.info.main }} />}
                    sx={{
                        mb: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        border: `1px solid ${theme.palette.info.main}`,
                        '& .MuiAlert-message': {
                            color: theme.palette.info.main,
                        },
                    }}
                >
                    <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                        Diese Rollen basieren auf Ihren Fähigkeiten und Erfahrungen.
                    </Typography>
                </Alert>
                <List>
                    {roles.map((item) => (
                        <ListItem key={item.id} sx={{ px: 0, py: 1 }}>
                            <ListItemText primary={item.name} sx={{ mr: 2 }} />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {item.status === 'pending' && (
                                    <>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                color: '#0BB140',
                                                borderColor: '#0BB140',
                                                '&:hover': {
                                                    backgroundColor: '#0BB140',
                                                    color: 'white',
                                                },
                                            }}
                                            onClick={() => handleStatusChange('roles', item.id, 'accepted')}
                                        >
                                            Annehmen
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                borderColor: theme.palette.primary.main,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: 'white',
                                                },
                                            }}
                                            onClick={() => handleStatusChange('roles', item.id, 'rejected')}
                                        >
                                            Ablehnen
                                        </Button>
                                    </>
                                )}
                                {item.status === 'accepted' && (
                                    <Chip
                                        icon={<CheckCircle />}
                                        label="Angenommen"
                                        className="status-chip-accepted"
                                        size="small"
                                        onClick={() => handleStatusChange('roles', item.id, 'pending')}
                                    />
                                )}
                                {item.status === 'rejected' && (
                                    <Chip
                                        icon={<Cancel />}
                                        label="Abgelehnt"
                                        className="status-chip-rejected"
                                        size="small"
                                        onClick={() => handleStatusChange('roles', item.id, 'pending')}
                                    />
                                )}
                            </Box>
                        </ListItem>
                    ))}
                </List>
            </CardContent>
        </Card>
    );

    const renderCurrentSubsection = () =>
    {
        switch (currentSubsection)
        {
            case 0:
                return renderPersonalInfoSection();
            case 1:
                return renderSkillsSection();
            case 2:
                return renderSuperPowersSection();
            case 3:
                return renderCertificatesSection();
            case 4:
                return renderRolesSection();
            default:
                return renderPersonalInfoSection();
        }
    };

    const canProceed = () =>
    {
        switch (currentSubsection)
        {
            case 0:
                return (personalInfo.firstName ?? '').trim() && (personalInfo.lastName ?? '').trim();
            case 1:
                return skills.some((s) => s.status === 'accepted');
            case 2:
            case 3:
            case 4:
            case 5:
                return true;
            default:
                return false;
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                CV Analyse Ergebnisse - Schritt {currentSubsection + 1} von 5
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {renderCurrentSubsection()}

                    {/* Content Navigation Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 4,
                            pt: 3,
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Button variant="outlined" onClick={onBack} disabled={currentSubsection === 0} sx={{ px: 3 }}>
                            Zurück
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() =>
                            {
                                // Reset all data and go back to CV upload
                                setSkills([]);
                                setCertificates([]);
                                setRoles([]);
                                setSuperPowers([]);
                                setPersonalInfo({
                                    firstName: employee?.firstName || '',
                                    lastName: employee?.lastName || '',
                                    shortDescription: employee?.description || '',
                                });
                                // Navigate back to step 2 (CV upload)
                                window.location.hash = '#step-2';
                            }}
                            sx={{
                                color: theme.palette.warning.main,
                                borderColor: theme.palette.warning.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.warning.main,
                                    color: 'white',
                                },
                            }}
                        >
                            CV Analyse neu starten
                        </Button>

                        <Button
                            variant="contained"
                            onClick={onNext}
                            disabled={!canProceed()}
                            sx={{
                                px: 4,
                                background: canProceed()
                                    ? `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`
                                    : undefined,
                            }}
                        >
                            {currentSubsection === 4 ? 'Zur Ergebnisübersicht' : 'Weiter'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
export default CVAnalysisResultsStep;
